import { ChevronDown } from 'lucide-react';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

import { useSession, useSignOut } from '@platform/api-client/react';
import { cn } from '@platform/ui';

/** `user.role` as it reads in a menu, not as the database spells it. */
const ROLE_LABELS: Record<string, string> = {
  clinic_admin: 'Clinic administrator',
  clinic_staff: 'Clinic staff',
  platform_admin: 'Platform administrator',
  platform_support: 'Platform support',
};

export interface AccountMenuProps {
  /** Renders the "Security" entry as the router's Link. */
  renderLink: (props: {
    to: string;
    className: string;
    children: ReactNode;
    onClick: () => void;
  }) => ReactNode;
}

/**
 * The top bar's account menu (M2.7, FR-UI-05).
 *
 * A disclosure, not a `role="menu"`. The ARIA menu pattern brings type-ahead, arrow-key
 * ownership and an implied application mode for what is, here, one link and one button -
 * and a half-implemented menu role is worse for a screen-reader user than a plainly
 * labelled button that reveals a short list (rules §48.2). What it does implement is the
 * part people actually rely on: Escape closes and returns focus, a click outside closes,
 * and the trigger carries `aria-expanded`.
 *
 * The **clinic** is named in the panel as well as the role. Staff at a practice with two
 * sites, and a support engineer inside an impersonated session, both need to be able to
 * answer "which clinic am I in" without reading the address bar - and the answer comes
 * from `session.tenant`, which the server resolved from the host, never from anything
 * the page chose.
 *
 * Signing out clears the whole query cache. That matters here more than anywhere: the
 * cache holds patient data, and leaving it in memory for the next person at a shared
 * front desk is exactly what session expiry exists to prevent (ADR-007).
 */
export function AccountMenu({ renderLink }: AccountMenuProps) {
  const session = useSession();
  const signOut = useSignOut();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event: MouseEvent): void => {
      if (containerRef.current?.contains(event.target as Node) !== true) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      triggerRef.current?.focus();
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const user = session.data?.authenticated === true ? session.data.user : null;
  if (user === null || user === undefined) {
    return <span className="text-xs text-neutral-400-text">Signed out</span>;
  }

  const roleLabel = user.role === null ? 'Clinic staff' : (ROLE_LABELS[user.role] ?? user.role);
  const name = user.name === '' ? user.email : user.name;
  const tenantName = session.data?.tenant?.name ?? null;

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => {
          setOpen((current) => !current);
        }}
        className="flex min-h-11 cursor-pointer items-center gap-2 rounded-input px-2 py-1 text-left hover:bg-neutral-50"
      >
        <span className="flex flex-col">
          <span className="text-sm font-semibold text-ink">{name}</span>
          <span className="text-xs text-neutral-400-text">{roleLabel}</span>
        </span>
        <ChevronDown
          size={16}
          strokeWidth={2}
          aria-hidden={true}
          className={cn('text-neutral-400-text transition-transform', open ? 'rotate-180' : '')}
        />
      </button>

      <div
        id={menuId}
        hidden={!open}
        className="absolute right-0 z-30 mt-1 w-64 overflow-hidden rounded-card border border-neutral-200 bg-surface py-1 shadow-dropdown"
      >
        <p className="truncate px-4 py-2 text-xs text-neutral-400-text">{user.email}</p>
        {tenantName === null ? null : (
          <p className="truncate border-b border-neutral-100 px-4 pb-2 text-xs text-neutral-400-text">
            {tenantName}
          </p>
        )}

        {renderLink({
          to: '/account/security',
          className:
            'block w-full px-4 py-[10px] text-left text-sm text-neutral-700 hover:bg-neutral-50',
          children: 'Security',
          onClick: () => {
            setOpen(false);
          },
        })}

        <button
          type="button"
          className="block w-full cursor-pointer px-4 py-[10px] text-left text-sm text-neutral-700 hover:bg-neutral-50"
          onClick={() => {
            setOpen(false);
            signOut.mutate();
          }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
